import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AlertIcon, CheckIcon } from '../icons';

/** הודעה קצרה בתחתית המסך ("הקישור הועתק"). מעל הניווט התחתון בנייד. */

type Tone = 'success' | 'error';
interface ToastState {
  id: number;
  text: string;
  tone: Tone;
}

const ToastContext = createContext<(text: string, tone?: Tone) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((text: string, tone: Tone = 'success') => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ id: Date.now(), text, tone });
    timer.current = setTimeout(() => setToast(null), tone === 'error' ? 5000 : 2500);
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex justify-center px-4 md:bottom-8">
        {toast && (
          <div
            key={toast.id}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            className={[
              'pointer-events-auto flex max-w-md items-center gap-2 rounded-control px-4 py-3 text-[14px] font-medium text-white shadow-lg',
              toast.tone === 'error' ? 'bg-danger' : 'bg-brand',
            ].join(' ')}
          >
            {toast.tone === 'error' ? <AlertIcon size={18} /> : <CheckIcon size={18} />}
            {toast.text}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
